import { Controller } from '@hotwired/stimulus'

// Keeps the plan wizard stable while an auto-saved change is being persisted.
// The dropdowns save with a short debounce (rapid changes within it coalesce into
// one save), then the form re-renders with refreshed options.
//
// Two protections during that in-flight window:
//  - "next" targets: locked so the wizard can't advance before the save lands,
//    and only unlocked once a save covering the latest change completes
//    (pendingChange tracks a change that arrived mid-flight).
//  - "field" targets (the dropdowns): disabled at turbo:submit-start. Turbo has
//    already serialized the form by then, so disabling can't drop a value; it
//    stops the teacher picking soon-to-be-stale options (and losing their
//    selection) while dependent dropdowns are being refreshed. The re-render
//    brings them back enabled; submit-end re-enables them if no render follows.
export default class extends Controller {
  static targets = ['next', 'field']

  connect() {
    this.pendingChange = false
    this.markSubmitting = this.markSubmitting.bind(this)
    this.onSubmitEnd = this.onSubmitEnd.bind(this)
    this.element.addEventListener('turbo:submit-start', this.markSubmitting)
    this.element.addEventListener('turbo:submit-end', this.onSubmitEnd)
  }

  disconnect() {
    this.element.removeEventListener('turbo:submit-start', this.markSubmitting)
    this.element.removeEventListener('turbo:submit-end', this.onSubmitEnd)
  }

  lock() {
    this.pendingChange = true
    this.#setNextLocked(true)
  }

  markSubmitting() {
    // The request now starting reflects the latest change, so clear the flag.
    // Any change that lands while it's in flight will set it again.
    this.pendingChange = false
    this.fieldTargets.forEach((el) => { el.disabled = true })
  }

  onSubmitEnd(event) {
    // Only release fields we locked for the in-flight save. Server-disabled
    // fields carry aria-disabled, so they stay frozen whether this runs before
    // or after the re-render applies fresh server state.
    this.fieldTargets.forEach((el) => {
      if (!el.hasAttribute('aria-disabled')) el.disabled = false
    })

    // A newer change is still waiting to be saved — keep Next locked.
    if (this.pendingChange) return

    // A failed save (network error, 5xx) leaves the dropdowns showing values the
    // server never accepted — advancing then would use the stale saved plan.
    if (event.detail && !event.detail.success) return

    // A successful save always re-renders (redirect or 422), and only that fresh
    // markup knows Next's real state — e.g. deselecting every lesson must leave
    // Next disabled. Unlocking now would reopen the stale link during the
    // response-to-render window, so unlock only once the render has landed (a
    // full page replace makes this a no-op on the detached old element).
    document.addEventListener('turbo:render', () => this.#setNextLocked(false), { once: true })
  }

  #setNextLocked(locked) {
    this.nextTargets.forEach((el) => {
      el.classList.toggle('pointer-events-none', locked)
      el.classList.toggle('opacity-50', locked)
      if (locked) {
        el.setAttribute('aria-disabled', 'true')
        el.setAttribute('tabindex', '-1')
      } else {
        el.removeAttribute('aria-disabled')
        el.removeAttribute('tabindex')
      }
    })
  }
}
